import { supabase } from '@/lib/supabase'
import type { Row } from '@/lib/supabase'

/**
 * Every query here is scoped by organisation_id even though row level
 * security already scopes it. Two reasons: a policy change should not
 * silently widen a screen, and the composite foreign keys in this
 * schema all carry organisation_id, so passing it keeps the query
 * planner on the right index.
 */

export type EmploymentStatus = 'active' | 'pending' | 'left'
export type DateSource = 'integration' | 'defaulted' | 'hr_confirmed'
export type Severity = 'critical' | 'overdue' | 'due' | 'due_soon' | 'info'

function fail(context: string, error: { message: string } | null): never {
  throw new Error(`${context}: ${error?.message ?? 'unknown error'}`)
}

/* ── organisations the signed-in user can see ─────────────── */

export type AppRole =
  | 'group_admin'
  | 'org_admin'
  | 'hr'
  | 'manager'
  | 'viewer'
  | 'front_desk'

/**
 * Fields beyond id, code and name are nullable because a front_desk user
 * cannot read the `organisations` table at all. Reception needs to know which
 * building they are in; it does not need the company's leave year or timezone,
 * and a screen that shows a blank there is correct rather than broken.
 */
export interface Org {
  id: string
  code: string
  legalName: string
  tradingName: string | null
  timezone: string | null
  leaveYearStartMonth: number | null
  leaveYearStartDay: number | null
  activeHeadcount: number | null
  roles: AppRole[]
}

export async function listOrganisations(): Promise<Org[]> {
  // my_org_access is SECURITY DEFINER and answers for EVERY role, including
  // front_desk, which holds no select on `organisations` by design (0026, and
  // 0032 which exists because that design would otherwise have locked
  // reception out of the app entirely).
  const access = await supabase.rpc('my_org_access' as never)
  if (access.error) fail('Could not load your organisations', access.error)

  const rows = (access.data ?? []) as unknown as {
    organisation_id: string
    code: string
    name: string
    roles: AppRole[]
  }[]
  if (rows.length === 0) return []

  const base: Org[] = rows.map((r) => ({
    id: r.organisation_id,
    code: r.code,
    legalName: r.name,
    tradingName: null,
    timezone: null,
    leaveYearStartMonth: null,
    leaveYearStartDay: null,
    activeHeadcount: null,
    roles: r.roles ?? [],
  }))

  // The rest needs table reads. Reception cannot make them, and that is not a
  // failure: the extra fields stay null and the screens leave them out. Only
  // an unreadable table is tolerated here, not a broken query.
  const detail = await supabase
    .from('organisations')
    .select('id, legal_name, trading_name, timezone, leave_year_start_month, leave_year_start_day')
    .eq('status', 'active')

  if (detail.error !== null || detail.data === null) return base

  const byId = new Map(detail.data.map((o) => [o.id as string, o]))
  const enriched = await Promise.all(
    base.map(async (org) => {
      const d = byId.get(org.id)
      if (d === undefined) return org
      const { count } = await supabase
        .from('employments')
        .select('id', { count: 'exact', head: true })
        .eq('organisation_id', org.id)
        .eq('status', 'active')
      return {
        ...org,
        legalName: (d.legal_name as string) ?? org.legalName,
        tradingName: (d.trading_name as string | null) ?? null,
        timezone: (d.timezone as string | null) ?? null,
        leaveYearStartMonth: (d.leave_year_start_month as number | null) ?? null,
        leaveYearStartDay: (d.leave_year_start_day as number | null) ?? null,
        activeHeadcount: count ?? null,
      }
    }),
  )
  return enriched
}

/* ── the Today screen ─────────────────────────────────────── */

export interface FlagGroup {
  ruleCode: string
  severity: Severity
  total: number
  /** At most three, so a flood of 103 does not become a wall of names. */
  sample: string[]
}

export interface Overview {
  headcount: { active: number; pending: number; left: number }
  departments: { name: string; headcount: number }[]
  flags: { total: number; bySeverity: Record<string, number>; groups: FlagGroup[] }
  tasks: { total: number; byRole: Record<string, number> }
  unconfirmedDates: number
  exemptions: { personId: string; name: string; reason: string }[]
  lastSync: { finishedAt: string | null; seen: number; changed: number; skipped: number } | null
  writesAllowed: boolean
}

export async function loadOverview(organisationId: string): Promise<Overview> {
  const [
    employments,
    departments,
    flags,
    tasks,
    exemptions,
    lastRun,
    connection,
  ] = await Promise.all([
    supabase.from('employments').select('status, department_id').eq('organisation_id', organisationId),
    supabase.from('departments').select('id, name').eq('organisation_id', organisationId).order('name'),
    supabase
      .from('flags')
      .select('rule_code, severity, person_id, people(first_name, last_name)')
      .eq('organisation_id', organisationId)
      .is('resolved_at', null),
    supabase.from('tasks').select('assigned_role').eq('organisation_id', organisationId).eq('status', 'open'),
    supabase
      .from('people')
      .select('id, first_name, last_name, compliance_exempt_reason')
      .eq('organisation_id', organisationId)
      .not('compliance_exempt_reason', 'is', null),
    supabase
      .from('sync_runs')
      .select('finished_at, records_seen, records_changed, records_skipped')
      .eq('organisation_id', organisationId)
      .eq('status', 'succeeded')
      .order('finished_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('integration_connections')
      .select('allow_writes')
      .eq('organisation_id', organisationId)
      .eq('system', 'planday')
      .maybeSingle(),
  ])

  if (employments.error) fail('Could not load employments', employments.error)
  if (departments.error) fail('Could not load departments', departments.error)
  if (flags.error) fail('Could not load flags', flags.error)
  if (tasks.error) fail('Could not load tasks', tasks.error)
  if (exemptions.error) fail('Could not load exemptions', exemptions.error)

  const headcount = { active: 0, pending: 0, left: 0 }
  const perDept = new Map<string, number>()
  for (const e of employments.data) {
    if (e.status === 'active') headcount.active += 1
    else if (e.status === 'pending') headcount.pending += 1
    else headcount.left += 1
    if (e.status === 'active' && e.department_id !== null) {
      perDept.set(e.department_id, (perDept.get(e.department_id) ?? 0) + 1)
    }
  }

  const bySeverity: Record<string, number> = {}
  const grouped = new Map<string, FlagGroup>()
  let unconfirmedDates = 0

  for (const f of flags.data) {
    bySeverity[f.severity] = (bySeverity[f.severity] ?? 0) + 1
    if (f.rule_code === 'unconfirmed_key_date') unconfirmedDates += 1

    const key = `${f.rule_code}:${f.severity}`
    let group = grouped.get(key)
    if (group === undefined) {
      group = { ruleCode: f.rule_code, severity: f.severity as Severity, total: 0, sample: [] }
      grouped.set(key, group)
    }
    group.total += 1
    const person = f.people as { first_name: string; last_name: string } | null
    if (group.sample.length < 3 && person !== null) {
      group.sample.push(`${person.first_name} ${person.last_name}`)
    }
  }

  const byRole: Record<string, number> = {}
  for (const t of tasks.data) {
    const role = t.assigned_role ?? 'unassigned'
    byRole[role] = (byRole[role] ?? 0) + 1
  }

  const severityRank: Severity[] = ['critical', 'overdue', 'due', 'due_soon', 'info']

  return {
    headcount,
    departments: departments.data.map((d) => ({
      name: d.name,
      headcount: perDept.get(d.id) ?? 0,
    })),
    flags: {
      total: flags.data.length,
      bySeverity,
      groups: [...grouped.values()].sort(
        (a, b) =>
          severityRank.indexOf(a.severity) - severityRank.indexOf(b.severity) ||
          b.total - a.total,
      ),
    },
    tasks: { total: tasks.data.length, byRole },
    unconfirmedDates,
    exemptions: exemptions.data.map((p) => ({
      personId: p.id,
      name: `${p.first_name} ${p.last_name}`,
      reason: p.compliance_exempt_reason ?? '',
    })),
    lastSync:
      lastRun.data === null || lastRun.error !== null
        ? null
        : {
            finishedAt: lastRun.data.finished_at,
            seen: lastRun.data.records_seen,
            changed: lastRun.data.records_changed,
            skipped: lastRun.data.records_skipped,
          },
    writesAllowed: connection.data?.allow_writes === true,
  }
}

/* ── the People screen ────────────────────────────────────── */

export interface PersonRow {
  employmentId: string
  personId: string
  name: string
  reference: string | null
  department: string | null
  position: string | null
  status: EmploymentStatus
  startDate: string
  startDateSource: DateSource
  endDate: string | null
  openFlags: number
}

export async function listPeople(
  organisationId: string,
  opts: { department?: string | undefined; search?: string | undefined } = {},
): Promise<PersonRow[]> {
  let query = supabase
    .from('employments')
    .select(
      `
      id, person_id, employee_reference, status, start_date, start_date_source, end_date,
      people!inner(first_name, last_name, preferred_name),
      departments(name),
      positions(title)
    `,
    )
    .eq('organisation_id', organisationId)
    .in('status', ['active', 'pending'])
    .order('start_date', { ascending: false })
    .limit(500)

  if (opts.search !== undefined && opts.search.trim() !== '') {
    const term = `%${opts.search.trim()}%`
    query = query.or(`first_name.ilike.${term},last_name.ilike.${term}`, {
      referencedTable: 'people',
    })
  }

  const { data, error } = await query
  if (error) fail('Could not load people', error)

  const flagCounts = await supabase
    .from('flags')
    .select('employment_id')
    .eq('organisation_id', organisationId)
    .is('resolved_at', null)
  if (flagCounts.error) fail('Could not count flags', flagCounts.error)

  const perEmployment = new Map<string, number>()
  for (const f of flagCounts.data) {
    if (f.employment_id === null) continue
    perEmployment.set(f.employment_id, (perEmployment.get(f.employment_id) ?? 0) + 1)
  }

  const rows: PersonRow[] = data.map((e) => {
    const person = e.people as { first_name: string; last_name: string; preferred_name: string | null }
    const dept = e.departments as { name: string } | null
    const pos = e.positions as { title: string } | null
    return {
      employmentId: e.id,
      personId: e.person_id,
      name: `${person.preferred_name ?? person.first_name} ${person.last_name}`,
      reference: e.employee_reference,
      department: dept?.name ?? null,
      position: pos?.title ?? null,
      status: e.status as EmploymentStatus,
      startDate: e.start_date,
      startDateSource: e.start_date_source as DateSource,
      endDate: e.end_date,
      openFlags: perEmployment.get(e.id) ?? 0,
    }
  })

  if (opts.department !== undefined && opts.department !== 'all') {
    return rows.filter((r) => r.department === opts.department)
  }
  return rows
}

/* ── one person ───────────────────────────────────────────── */

export interface PersonDetail {
  person: Pick<Row<'people'>, 'id' | 'first_name' | 'last_name' | 'preferred_name' | 'email' | 'compliance_exempt_reason'>
  employment: Pick<
    Row<'employments'>,
    | 'id'
    | 'employee_reference'
    | 'status'
    | 'start_date'
    | 'start_date_source'
    | 'end_date'
    | 'end_date_source'
    | 'leaver_reason'
    | 'probation_end_date'
    | 'continuous_service_date'
  >
  department: string | null
  /** Needed to write a review meeting, whose access policy is department-scoped. */
  departmentId: string | null
  position: string | null
  flags: { ruleCode: string; severity: Severity; title: string; detail: string | null; dueOn: string | null }[]
}

export async function loadPerson(
  organisationId: string,
  employmentId: string,
): Promise<PersonDetail> {
  const { data, error } = await supabase
    .from('employments')
    .select(
      `
      id, employee_reference, status, start_date, start_date_source, end_date, end_date_source,
      leaver_reason, probation_end_date, continuous_service_date, department_id,
      people!inner(id, first_name, last_name, preferred_name, email, compliance_exempt_reason),
      departments(name),
      positions(title)
    `,
    )
    .eq('organisation_id', organisationId)
    .eq('id', employmentId)
    .single()

  if (error) fail('Could not load this person', error)

  const flags = await supabase
    .from('flags')
    .select('rule_code, severity, title, detail, due_on')
    .eq('organisation_id', organisationId)
    .eq('employment_id', employmentId)
    .is('resolved_at', null)
  if (flags.error) fail('Could not load flags for this person', flags.error)

  const dept = data.departments as { name: string } | null
  const pos = data.positions as { title: string } | null

  return {
    person: data.people as PersonDetail['person'],
    employment: {
      id: data.id,
      employee_reference: data.employee_reference,
      status: data.status,
      start_date: data.start_date,
      start_date_source: data.start_date_source,
      end_date: data.end_date,
      end_date_source: data.end_date_source,
      leaver_reason: data.leaver_reason,
      probation_end_date: data.probation_end_date,
      continuous_service_date: data.continuous_service_date,
    },
    department: dept?.name ?? null,
    departmentId: data.department_id,
    position: pos?.title ?? null,
    flags: flags.data.map((f) => ({
      ruleCode: f.rule_code,
      severity: f.severity as Severity,
      title: f.title,
      detail: f.detail,
      dueOn: f.due_on,
    })),
  }
}

/* ── sync screen ──────────────────────────────────────────── */

export interface ConnectionRow {
  organisationCode: string
  displayName: string | null
  isActive: boolean
  allowWrites: boolean
  lastSuccessAt: string | null
  lastFailureAt: string | null
  lastRun: { seen: number; changed: number; skipped: number; failed: number } | null
}

export async function listConnections(): Promise<ConnectionRow[]> {
  const { data, error } = await supabase
    .from('integration_connections')
    .select('organisation_id, display_name, is_active, allow_writes, last_success_at, last_failure_at, organisations!inner(code)')
    .eq('system', 'planday')
  if (error) fail('Could not load connections', error)

  return Promise.all(
    data.map(async (c) => {
      const run = await supabase
        .from('sync_runs')
        .select('records_seen, records_changed, records_skipped, records_failed')
        .eq('organisation_id', c.organisation_id)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      const org = c.organisations as { code: string }
      return {
        organisationCode: org.code,
        displayName: c.display_name,
        isActive: c.is_active,
        allowWrites: c.allow_writes,
        lastSuccessAt: c.last_success_at,
        lastFailureAt: c.last_failure_at,
        lastRun:
          run.data === null
            ? null
            : {
                seen: run.data.records_seen,
                changed: run.data.records_changed,
                skipped: run.data.records_skipped,
                failed: run.data.records_failed,
              },
      }
    }),
  )
}
